import type { DebriefPresentation } from "../core/engine";

export type MissionStats = {
  shots: number;
  hits: number;
  kills: number;
  squadAlive: number;
  squadTotal: number;
};

// Pure presentation: stats plus whatever reward lines the owning flow
// computed. Meta/run state is never touched here (hud modules are read-only
// observers; meta mutates only in the run controller).
export class DebriefingHud {
  private readonly el: HTMLElement;
  private onContinue: (() => void) | null = null;

  constructor() {
    const el = document.createElement("div");
    el.id = "hud-debrief";
    el.className = "screen-overlay";
    el.style.pointerEvents = "none";
    el.style.opacity = "0";
    el.style.transition = "opacity 0.8s";
    el.style.zIndex = "100";
    document.body.appendChild(el);
    this.el = el;
  }

  show(success: boolean, stats: MissionStats, presentation: DebriefPresentation, onContinue: () => void): void {
    const accuracy = stats.shots > 0 ? Math.round((stats.hits / stats.shots) * 100) : 0;
    this.onContinue = onContinue;

    const titleColor = success ? "#50ff78" : "#ff5050";
    const titleText = success ? "MISSION COMPLETE" : "MISSION FAILED";
    const rewardHtml = presentation.rewardLines
      .map((line) => `<div class="debrief-earned">${line}</div>`)
      .join("");

    this.el.innerHTML = `
      <div class="screen-panel screen-panel--narrow">
        <div class="screen-title" style="color:${titleColor};margin-bottom:24px">${titleText}</div>
        <div class="debrief-stats">
          <div>SQUAD SURVIVED &nbsp; <span style="color:#c8ffdc">${stats.squadAlive}/${stats.squadTotal}</span></div>
          <div>ENEMIES DOWN &nbsp; <span style="color:#c8ffdc">${stats.kills}</span></div>
          <div>ROUNDS FIRED &nbsp; <span style="color:#c8ffdc">${stats.shots}</span></div>
          <div>ACCURACY &nbsp; <span style="color:#c8ffdc">${accuracy}%</span></div>
        </div>
        ${rewardHtml}
        <div class="screen-actions" style="margin-top:24px">
          <button id="debrief-continue" class="screen-button" style="pointer-events:auto">${presentation.continueLabel}</button>
        </div>
      </div>
    `;

    this.el.querySelector("#debrief-continue")?.addEventListener("click", () => {
      this.onContinue?.();
    });

    requestAnimationFrame(() => {
      this.el.classList.add("is-visible");
      this.el.style.opacity = "1";
    });
  }

  hide(): void {
    this.el.style.opacity = "0";
    this.el.classList.remove("is-visible");
  }

  dispose(): void {
    this.el.remove();
  }
}
